/**
 * App - Componente Raiz da Aplicação
 * 
 * CONTEXTO DE NEGÓCIO:
 * Componente principal que configura o roteamento da aplicação.
 * Define as rotas disponíveis e renderiza o layout base.
 * 
 * FUNCIONALIDADES:
 * - Configurar React Router com todas as rotas
 * - Envolver páginas com ComponenteLayout
 * - Fornecer navegação entre páginas
 * - Fornecer contexto global de modal para toda a aplicação (TAREFA-097)
 * - Fornecer contexto de organização para multi-tenant
 * 
 * RESPONSABILIDADES:
 * - Definir estrutura de rotas
 * - Compor layout com páginas
 * - Servir como ponto de entrada da aplicação
 * - Inicializar providers globais (ModalProvider, Autenticacao, Organizacao)
 */

import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ComponenteLayout } from './core/componentes/comuns/ComponenteLayout';
import { PaginaPrincipal } from './verticals/juridico/paginas/PaginaPrincipal';
import { PaginaLogin } from './core/paginas/PaginaLogin';
import { PaginaCadastro } from './core/paginas/PaginaCadastro';
import { PaginaRecuperarSenha } from './core/paginas/PaginaRecuperarSenha';
import { PaginaRedefinirSenha } from './core/paginas/PaginaRedefinirSenha';
import { PaginaAdmin } from './core/paginas/PaginaAdmin';
import { PaginaOrganizacao } from './core/paginas/PaginaOrganizacao';
import { PaginaConvite } from './core/paginas/PaginaConvite';
import { DashboardProcessos } from './verticals/juridico/paginas/DashboardProcessos';
// Página de Upload aposentada: rota /upload redireciona para /analise
// import { PaginaUpload } from './paginas/PaginaUpload';
import { PaginaAnalise } from './verticals/juridico/paginas/PaginaAnalise';
import { PaginaHistorico } from './verticals/juridico/paginas/PaginaHistorico';
import { AnalisePeticaoInicial } from './verticals/juridico/paginas/AnalisePeticaoInicial';
import CriacaoPeticaoInicial from './verticals/juridico/paginas/CriacaoPeticaoInicial';
import ProcessoDetalhe from './verticals/juridico/paginas/ProcessoDetalhe';
import { ModalProvider } from './core/contextos/ModalProvider';
import { ProvedorAutenticacao } from './core/contextos/ContextoAutenticacao';
import { ProvedorOrganizacao } from './core/contextos/ContextoOrganizacao';
import { RotaProtegida } from './core/componentes/auth/RotaProtegida';
import { RotaProtegidaAdmin } from './core/componentes/auth/RotaProtegidaAdmin';

/**
 * Componente principal da aplicação
 * 
 * IMPLEMENTAÇÃO:
 * - ModalProvider envolve toda a aplicação para fornecer sistema de modals (TAREFA-097)
 * - ProvedorAutenticacao gerencia estado de autenticação
 * - ProvedorOrganizacao gerencia estado de organização/multi-tenant
 * - BrowserRouter para navegação baseada em histórico
 * - Routes para definir mapeamento de rotas
 * - ComponenteLayout envolve todas as páginas
 * 
 * ROTAS DISPONÍVEIS:
 * - / : Página inicial
 * - /upload : Upload de documentos
 * - /analise : Análise multi-agent
 * - /historico : Histórico de documentos
 * - /analise-peticao : Análise de petição inicial (NOVO - FASE 7)
 * - /processos/:id : Detalhe de processo específico
 * - /organizacao : Gerenciamento de organização (multi-tenant)
 * - /convite/:token : Aceitar convite de organização
 * 
 * PROVIDERS:
 * - ModalProvider: Gerencia modals de alerta/confirmação globalmente (TAREFA-097)
 * - ProvedorAutenticacao: Gerencia login/logout e estado do usuário
 * - ProvedorOrganizacao: Gerencia organização selecionada e membros
 */
function App() {
  return (
    <ModalProvider>
      {/* ProvedorAutenticacao: deve envolver a aplicação DEPOIS do ModalProvider */}
      <ProvedorAutenticacao>
        {/* ProvedorOrganizacao: requer autenticação, então vem DEPOIS */}
        <ProvedorOrganizacao>
          <Router>
            <Routes>
              {/* Rota: Convite (sem layout, fluxo especial) */}
              <Route path="/convite/:token" element={<PaginaConvite />} />
              
              {/* Rotas com Layout */}
              <Route element={<ComponenteLayout />}>
                {/* Rota: Página Principal (seleção de features) */}
                <Route path="/" element={<PaginaPrincipal />} />

                {/* Rota: Login */}
                <Route path="/login" element={<PaginaLogin />} />

                {/* Rota: Cadastro */}
                <Route path="/cadastro" element={<PaginaCadastro />} />

                {/* Rota: Recuperar Senha */}
                <Route path="/recuperar-senha" element={<PaginaRecuperarSenha />} />

                {/* Rota: Redefinir Senha (com token) */}
                <Route path="/redefinir-senha/:token" element={<PaginaRedefinirSenha />} />

                {/* Rota: Upload de Documentos (aposentada) - redirecionar para /analise */}
                <Route path="/upload" element={<Navigate to="/analise" replace />} />

                {/* Rota: Análise Multi-Agent */}
                <Route path="/analise" element={
                  <RotaProtegida>
                    <PaginaAnalise />
                  </RotaProtegida>
                } />

                {/* Rota: Dashboard de Processos (TAREFA-059) */}
                <Route path="/dashboard" element={
                  <RotaProtegida>
                    <DashboardProcessos />
                  </RotaProtegida>
                } />

                {/* Rota: Histórico de Documentos (protegida) */}
                <Route path="/historico" element={
                  <RotaProtegida>
                    <PaginaHistorico />
                  </RotaProtegida>
                } />

                {/* Rota: Análise de Petição Inicial (FASE 7 - TAREFA-049) */}
                <Route path="/analise-peticao" element={
                  <RotaProtegida>
                    <AnalisePeticaoInicial />
                  </RotaProtegida>
                } />

                {/* Rota: Criação de Petição Inicial (Reclamante) */}
                <Route path="/criacao-peticao-inicial" element={
                  <RotaProtegida>
                    <CriacaoPeticaoInicial />
                  </RotaProtegida>
                } />

                {/* Rota: Detalhe de Processo */}
                <Route path="/processos/:id" element={
                  <RotaProtegida>
                    <ProcessoDetalhe />
                  </RotaProtegida>
                } />

                {/* Rota: Gerenciamento de Organização (multi-tenant) */}
                <Route path="/organizacao" element={
                  <RotaProtegida>
                    <PaginaOrganizacao />
                  </RotaProtegida>
                } />

                {/* Rota: Painel de Administração (apenas admins) */}
                <Route path="/admin" element={
                  <RotaProtegidaAdmin>
                    <PaginaAdmin />
                  </RotaProtegidaAdmin>
                } />
              </Route>
            </Routes>
          </Router>
        </ProvedorOrganizacao>
      </ProvedorAutenticacao>
    </ModalProvider>
  );
}

export default App;

